<template>
  <div>
    <div class="row page-titles">
      <div class="col-md-5 align-self-center">
        <h4 class="text-themecolor">Designs</h4>
      </div>
      <div class="col-md-7 align-self-center text-right">
        <div class="d-flex justify-content-end align-items-center">
          <breadcrumb :page="currentPage"/>
          <design-create-button class="ml-2" @success="fetch"/>
        </div>
      </div>
    </div>
    <div class="row justify-content-md-end mt-4 mb-0">
      <div class="col-sm-6 col-md-4">
        <d-text-input
            type="text"
            style="background: white; margin-bottom: 8px"
            :value="search.name"
            placeholder="Search by name..."
            @input="value => { setSearchField({ key: 'name', value }) }"
        />
      </div>
      <div class="col-sm-6 col-md-2">
        <d-select-input
            style="background: white; margin-bottom: 0"
            name="status"
            :value="search.status"
            placeholder="Filter status"
            class-name="custom-select"
            :options="statusOptions"
            @input="value => { setSearchField({ key: 'status', value }) }"
        />
      </div>
      <div class="col-sm-6 col-md-1">
        <form-button
            :click="() => gotoSearch(search)"
            type="primary"
            :disabled="loading"
            class="form-control"
        >
          <i class="fa fa-search text-white"/>
          <span class="text-white ml-1">{{ $t('Search') }}</span>
        </form-button>
      </div>
    </div>
    <loading-container :loading="loading">
      <div class="text-center" v-if="!list || list.length === 0">
        No Transaction found
      </div>
      <div class="row" v-else>
        <div class="col-sm-12">
          <div class="card">
            <div class="table-responsive">
              <table
                  class="table table-striped dataTable no-footer"
                  style="width: 100%; margin-top: 0 !important;"
              >
                <thead>
                <tr>
                  <th>Design</th>
                  <th v-if="userRole !== 'designer'">Designer</th>
                  <th>Information</th>
                  <th>Last Updated</th>
                  <th>Status</th>
                  <th>Message</th>
                  <th></th>
                </tr>
                </thead>
                <tbody>
                <tr
                    :class="index % 2 === 0 ? 'odd' : 'even'"
                    v-for="(item, index) in list"
                    :key="index"
                >
                  <td>
                    <div class="d-f-r d-fa-c">
                      <d-img-light-box
                          v-if="get(item, 'thumb') && get(item, 'thumb').startsWith('https://drive')"
                          :url="getLinkImageDriver(get(item, 'thumb'))"/>
                      <d-img-light-box v-else :url="get(item, 'image') ||  get(item, 'thumb')"/>
                      <div class="ml-2 d-f-c">
                        <span> <strong>{{ get(item, 'name') }}</strong></span>
                      </div>
                    </div>
                  </td>
                  <td v-if="userRole !== 'designer'">
                    <div v-if="item.designer">
                      <div> {{ get(item, 'designer.name') }}</div>
                      <div> {{ get(item, 'designer.email') }}</div>
                    </div>
                    <div v-else class="text-muted">
                      <div>--/--</div>
                    </div>
                  </td>
                  <td>
                    <div v-if="item.designer">
                      <div>Material: {{ get(item, 'material') }}</div>
                      <div>Size: {{ get(item, 'size') }}</div>
                      <div style="max-width: 200px">Note: {{ get(item, 'note') }}</div>
                    </div>
                    <div v-else class="text-muted">
                      <div>--/--</div>
                    </div>
                  </td>
                  <td>
                    {{ item.updatedAt | datetimeDisplay }}
                  </td>
                  <td>
                    <design-assign-status :status="item.status"/>
                  </td>
                  <td>
                    <template v-if="item.messages && item.messages.length">
                      <div v-for="(note, index) in item.messages" :key="index" class="d-f-c mb-2">
                        <div>{{ note.message }}</div>
                        <div class="text-cyan font-12">{{ get(note, 'user.name') }}</div>
                        <span class="font-12 text-muted">{{ note.date | dateDisplay }}</span>
                      </div>
                    </template>
                  </td>
                  <td>
                    <div v-if="['admin', 'manager'].includes(userRole)" class="d-f-r d-fj-e">
                      <design-download-button v-if="item.status===4" :value="get(item, 'designs')"
                                              class="mr-2"/>
                      <design-assign-button :value="item" @success="fetch" class="mr-2"/>
                      <design-update-button :value="item" @success="fetch"/>
                    </div>
                    <div v-if="userRole === 'designer'" class="d-f-r d-fj-e">
                      <template v-if="item.status === 2">
                        <design-upload-button :value="item" @success="fetch"/>
                      </template>
                      <template v-else-if="item.status === 1">
                        <design-accept-button :value="item" @success="fetch" class="mr-2"/>
                        <design-reject-button :value="item" @success="fetch"/>
                      </template>
                      <template v-else-if="item.status===4">
                        <design-download-button :value="get(item, 'designs')"/>
                      </template>
                    </div>
                  </td>
                </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
      <div class="row">
        <div class="col-md-12 d-flex justify-content-center">
          <pagination
              :routeName="routeName"
              :query="search"
              :current="Number(page)"
              :total="total"
          />
        </div>
      </div>
    </loading-container>
  </div>
</template>
<style scoped>
img {
  width: 64px;
  height: 64px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  object-fit: cover;
}
</style>

<script>
import {mapActions, mapGetters} from "vuex";
import {Datetime} from 'vue-datetime';

import LoadingContainer from "@/components/Misc/LoadingContainer";
import InputSelect from "@/components/Form/InputSelect";
import InputText from "@/components/Form/InputText";
import Pagination from "@/components/Misc/Pagination";
import LinkButton from "@/components/Misc/LinkButton";
import FormButton from "@/components/Form/FormButton";
import Breadcrumb from "@/components/Common/Breadcrumb";
import Uuid from "@/components/Common/Uuid";
import TransactionAmount from "@/components/Common/TransactionAmount";
import PageMixin from "@/mixins/PageMixin";
import DesignCostStatus from "@/components/Views/Design/DesignCostStatus";
import Modal from "@/components/Misc/Modal";
import DesignAssignButton from "@/components/Views/Design/DesignAssignButton";
import DesignAssignStatus from "@/components/Views/Design/DesignAssignStatus";
import DesignUpdateButton from "@/components/Views/Design/DesignUpdateButton";
import DesignAcceptButton from "@/components/Views/Design/DesignAcceptButton";
import DesignRejectButton from "@/components/Views/Design/DesignRejectButton";
import DesignUploadButton from "@/components/Views/Design/DesignUploadButton";
import DesignDownloadButton from "@/components/Views/Design/DesignDownloadButton";
import DesignCreateButton from "@/components/Views/Design/DesignCreateButton";
import DTextInput from "@/components/input/DTextInput";
import DSelectInput from "@/components/input/DSelectInput";
import DImgLightBox from "@/components/Common/DImgLightBox";

export default {
  name: "DesignAssign",
  mixins: [PageMixin],
  components: {
    DImgLightBox,
    DSelectInput,
    DTextInput,
    DesignCreateButton,
    DesignDownloadButton,
    DesignUploadButton,
    DesignRejectButton,
    DesignAcceptButton,
    DesignUpdateButton,
    DesignAssignStatus,
    DesignAssignButton,
    Modal,
    DesignCostStatus,
    Datetime,
    LoadingContainer,
    InputSelect,
    Pagination,
    LinkButton,
    InputText,
    FormButton,
    Breadcrumb,
    Uuid,
    TransactionAmount,
  },
  beforeMount() {
    this.fetch()
  },
  computed: {
    ...mapGetters({
      list: "designAssign/list",
      search: "designAssign/search",
      loading: "designAssign/loading",
      count: "designAssign/count",
      total: "designAssign/total",
      page: "designAssign/page"
    }),
    statusOptions() {
      if (this.userRole === 'designer') {
        return [
          {value: 1, text: 'Assigned'},
          {value: 2, text: 'Designing'},
          {value: 3, text: 'Rejected'},
          {value: 4, text: 'Available'},
        ];
      }
      return [
        {value: 0, text: 'Not Available'},
        {value: 1, text: 'Assigned'},
        {value: 2, text: 'Designing'},
        {value: 3, text: 'Rejected'},
        {value: 4, text: 'Available'},
      ];
    },
  },
  methods: {
    fetch() {
      const query = this.$route.query;
      const page = query.page || 1;
      const status = query.status;
      const name = query.name;

      this.getList({
        page,
        status,
        name,
      });
    },
    getIdFromUrl(url) {
      return url.match(/[-\w]{25,}/);
    },
    getLinkImageDriver(link) {
      const str = this.getIdFromUrl(link)
      const result = "https://drive.google.com/uc?export=view&id=" + str
      console.log(result);
      return result;
    },
    ...mapActions({
      getList: "designAssign/getDesignEmbs",
      setSearchField: "designAssign/setSearchField",
    }),
  }
};
</script>